import { useCallback } from 'react';
import { sleep } from '@/lib/utils';

/**
 * R = retorno de transform;
 * M = shape de metadados extras.
 */
export type Message<R = any, M extends object = {}> = M & {
  key: string;
  command: string;
  transform?: (raw: any) => R;
  timeout?: number;
};

export type MessageV2<M extends object = {}> = M & {
  command: string;
};

export interface Props<Transport, M extends object = {}> {
  openTransport: (transport: Transport) => Promise<void>;
  closeTransport: (transport: Transport) => Promise<void>;
  sendMessage: (transport: Transport, msg: Message<any, M>) => Promise<any>;
  sendMessageV2: (transport: Transport, msg: MessageV2<M>) => Promise<any>;
  options?: {
    delayBetweenMessages?: number;
    maxRetriesPerMessage?: number;
    maxOverallRetries?: number;
  };
}

class RetryableMessageError extends Error {
  constructor(
    message: string,
    readonly meta: { command: string; attempt: number; cause?: unknown },
  ) {
    super(message);
    this.name = 'RetryableMessageError';
  }
}

class RetryLimitExceededError extends Error {
  constructor(
    message: string,
    readonly meta?: { command?: string; attempts?: number },
  ) {
    super(message);
    this.name = 'RetryLimitExceededError';
  }
}

export function useCommunication<Transport, M extends object = {}>(props: Props<Transport, M>) {
  const { openTransport, closeTransport, sendMessage, sendMessageV2, options = {} } = props;

  const { delayBetweenMessages = 150, maxRetriesPerMessage = 3, maxOverallRetries = 2 } = options;

  const sendSingleMessage = useCallback(
    async <R>(transport: Transport, msg: Message<R, M>): Promise<R | undefined> => {
      let attempts = 0;
      while (attempts < maxRetriesPerMessage) {
        try {
          const raw = await sendMessage(transport, msg);
          if (raw != null && msg.transform) {
            return msg.transform(raw);
          } else if (raw != null) {
            return raw;
          }
          return undefined;
        } catch (error) {
          const err = new RetryableMessageError(
            `Erro ao enviar mensagem "${msg.command}", tentativa ${attempts + 1}.`,
            { command: msg.command, attempt: attempts + 1, cause: error },
          );
          console.error(err);
        }
        attempts++;
      }

      throw new RetryLimitExceededError(
        `Limite de tentativas excedido para comando "${msg.command}".`,
        { command: msg.command, attempts },
      );
    },
    [maxRetriesPerMessage, sendMessage],
  );

  const sendMultipleMessages = useCallback(
    async <Msgs extends readonly Message<any, M>[]>(input: {
      transport: Transport;
      messages: Msgs;
      depth?: number;
      delayBetweenMessages?: number; // Override default delay
    }): Promise<{
      [P in Msgs[number] as P['key']]: P['transform'] extends (r: any) => infer R ? R : any;
    }> => {
      const { transport, messages, depth = 0, delayBetweenMessages: overrideDelay } = input;
      const actualDelay = overrideDelay ?? delayBetweenMessages;
      if (depth >= maxOverallRetries) {
        throw new RetryLimitExceededError('Limite máximo de tentativas para grupo de mensagens.', {
          attempts: depth,
        });
      }

      const results = {} as any;
      let transportOpened = false;
      try {
        await openTransport(transport);
        transportOpened = true;

        for (let i = 0; i < messages.length; i++) {
          const msg = messages[i]!;
          const res = await sendSingleMessage(transport, msg);
          results[msg.key] = res;
          // A resposta já foi lida dentro de sendSingleMessage — o delay só é necessário
          // ENTRE mensagens; após a última seria tempo morto.
          if (i < messages.length - 1) await sleep(actualDelay);
        }

        // Only close if we successfully opened it
        if (transportOpened) {
          try {
            await closeTransport(transport);
          } catch (closeError) {
            // Log but don't throw if close fails during success path
            console.warn('Warning: Failed to close transport after success:', closeError);
          }
        }
        return results;
      } catch (_err) {
        // Try to close, but ignore errors - the transport might already be closed
        try {
          if (transportOpened) {
            await closeTransport(transport);
          }
        } catch (_closeError) {
          // Ignore close errors during failure handling
          console.debug('Transport already closed or failed to close during error handling');
        }
        // Repassa o override de delay: sem ele a retentativa rodava com o
        // default (150ms), mais agressivo que o timing pedido pelo chamador —
        // justamente no caminho em que a comunicação já se mostrou instável.
        return sendMultipleMessages({
          transport,
          messages,
          depth: depth + 1,
          delayBetweenMessages: overrideDelay,
        });
      }
    },
    [openTransport, closeTransport, sendSingleMessage, delayBetweenMessages, maxOverallRetries],
  );

  const send = useCallback(
    async <Msgs extends readonly MessageV2<M>[], PRE = undefined, POS = undefined>(input: {
      transport: Transport;
      messages: Msgs;
      preChunk?: (transport: Transport) => Promise<PRE>;
      posChunk?: (transport: Transport) => Promise<POS>;
      delayBetweenMessages?: number; // Override default delay
    }): Promise<{ pre?: PRE; pos?: POS }> => {
      const {
        transport,
        messages,
        preChunk,
        posChunk,
        delayBetweenMessages: overrideDelay,
      } = input;
      const actualDelay = overrideDelay ?? delayBetweenMessages;
      let transportOpened = false;
      try {
        await openTransport(transport);
        transportOpened = true;

        const pre = preChunk ? await preChunk(transport) : undefined;

        for (const msg of messages) {
          await sendMessageV2(transport, msg);
          await sleep(actualDelay);
        }

        const pos = posChunk ? await posChunk(transport) : undefined;

        return { pre, pos };
      } finally {
        // Only try to close if we successfully opened it
        if (transportOpened) {
          try {
            await closeTransport(transport);
          } catch (_closeError) {
            console.debug('Transport already closed or failed to close during cleanup');
          }
        }
      }
    },
    [openTransport, closeTransport, delayBetweenMessages, sendMessageV2],
  );

  return { sendMultipleMessages, sendSingleMessage, send };
}

export default useCommunication;
